// src/pages/PftSurveyAnswers.js
//
// PFT Uploads card: "Medical history" — what the patient answered on the health
// survey, and their signed consent (flag PFT_SURVEY_TEXT_ENABLED). Read-only. Data:
// pft_survey_submissions/{id} (pftSurveyService.loadSurveySubmission); wording:
// pftSurveyAnswers.js.

import { useEffect, useState } from "react";
import { ExternalLink, HeartPulse } from "lucide-react";
import { loadSurveySubmission } from "../services/pftSurveyService";
import { differsFromPft, surveyConditions, surveyConditionsToDiagnosis, surveyWeightLines } from "../services/pftSurveyAnswers";
import { diagnosisLabel } from "../constants/diseaseOptions";

const fmtSubmitted = (ts) => {
  const d = ts && typeof ts.toDate === "function" ? ts.toDate() : ts ? new Date(ts) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
    : "";
};

export default function PftSurveyAnswers({ submissionId, card }) {
  const [state, setState] = useState({ loading: true, data: null, error: false });

  useEffect(() => {
    let live = true;
    setState({ loading: true, data: null, error: false });
    loadSurveySubmission(submissionId)
      .then((data) => live && setState({ loading: false, data, error: false }))
      .catch(() => live && setState({ loading: false, data: null, error: true }));
    return () => {
      live = false;
    };
  }, [submissionId]);

  const s = state.data;
  const conditions = s ? surveyConditions(s) : [];
  const weight = s ? surveyWeightLines(s) : [];
  const contact = s
    ? [
        { key: "phone", label: "Phone", value: s.phone, pft: card.phone },
        { key: "email", label: "Email", value: s.email, pft: "" },
        { key: "address", label: "Address", value: s.address, pft: card.address },
      ]
    : [];

  return (
    <div className="rounded-xl border p-3 space-y-2 pft-survey-answers">
      <p className="text-[13px] font-semibold flex items-center gap-1.5">
        <HeartPulse className="w-4 h-4" />
        Medical history (from the health survey)
      </p>
      {state.loading ? (
        <p className="text-[12px] text-slate-400">Loading the patient's answers…</p>
      ) : state.error || !s ? (
        <p className="text-[12px] text-amber-200">Couldn't load the patient's answers. Try reopening the card.</p>
      ) : (
        <div className="space-y-2 text-[12px] text-slate-200">
          <p className="text-slate-400">
            Answered {fmtSubmitted(s.submittedAt) || "—"} · signed by {s.printedName || "—"}
            {s.signedByRepresentative ? ` (${s.signerRelationshipLabel || "a representative"})` : ""}
          </p>
          <div>
            <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-0.5">Conditions</span>
            {conditions.length ? (
              <ul className="list-disc ml-5 space-y-0.5">
                {conditions.map((c, i) => (
                  <li key={i}>
                    {c.label}
                    {c.detail ? <span className="text-slate-400"> · {c.detail}</span> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-400">None of the listed conditions.</p>
            )}
          </div>
          {weight.length > 0 && (
            <div>
              <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-0.5">Weight</span>
              {weight.map((l) => (
                <p key={l}>{l}</p>
              ))}
            </div>
          )}
          <div>
            <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-0.5">
              Contact details they gave
            </span>
            {contact.map((c) => (
              <p key={c.key}>
                {c.label}: {c.value || "—"}
                {differsFromPft(c.key, c.value, c.pft) && (
                  <span className="pft-survey-differs"> · differs from the PFT ({c.pft})</span>
                )}
              </p>
            ))}
          </div>
          {card.status === "new" && (
            <p className="text-slate-300">
              When added as a new patient: diagnosis{" "}
              {surveyConditionsToDiagnosis(s.conditions).map(diagnosisLabel).join(", ") || "none from the survey"}; the
              phone, address and email they gave replace the PFT's (a blank answer keeps the PFT's, and replaced
              values go in the notes); this consent becomes their consent file and is also listed under Other files.
              Change anything on the patient page
              afterwards.
            </p>
          )}
          {s.consentPdfUrl && (
            <a
              href={s.consentPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[12px] text-slate-200 transition"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open signed consent PDF
            </a>
          )}
        </div>
      )}
    </div>
  );
}
